import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import { bigqueryApi } from '../../api/bigqueryApi';
import { datasetApi } from '../../api/datasetApi';
import { udfApi } from '../../api/udfApi';
import { workspaceApi } from '../../api/workspaceApi';
import { cn } from '../../lib/utils';

const OnboardingBanner = ({ refreshKey }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [status, setStatus] = useState({
    hasActiveConnection: false,
    hasImportedAssets: false,
    hasWorkspace: false
  });

  useEffect(() => {
    let isMounted = true;

    const loadOnboardingStatus = async () => {
      try {
        const [bqStatus, workspaces] = await Promise.all([
          bigqueryApi.getStatus(),
          workspaceApi.getWorkspaces()
        ]);

        // Always check for datasets, regardless of connection status
        const [datasets, functions] = await Promise.all([
          datasetApi.getDatasets().catch(() => []),
          bqStatus?.connected
            ? udfApi.listUDFs().catch(() => [])
            : Promise.resolve([])
        ]);

        if (!isMounted) {
          return;
        }

        setStatus({
          hasActiveConnection: !!bqStatus?.connected,
          hasImportedAssets: datasets.length > 0 || functions.length > 0,
          hasWorkspace: workspaces.length > 0
        });
      } catch (err) {
        console.error('Failed to load onboarding status:', err);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOnboardingStatus();

    // Listen for dataset updates
    const handleDatasetUpdate = () => {
      loadOnboardingStatus();
    };
    window.addEventListener('datasetAdded', handleDatasetUpdate);
    window.addEventListener('datasetRemoved', handleDatasetUpdate);

    return () => {
      isMounted = false;
      window.removeEventListener('datasetAdded', handleDatasetUpdate);
      window.removeEventListener('datasetRemoved', handleDatasetUpdate);
    };
  }, [refreshKey]);

  const currentStep = useMemo(() => {
    if (!status.hasActiveConnection) {
      return {
        number: 1,
        title: 'Step 1. Add a connection.',
        description: 'Connect BigQuery to unlock imports.',
        ctaLabel: 'Add Connection',
        ctaTo: '/sources'
      };
    }

    if (!status.hasImportedAssets) {
      return {
        number: 2,
        title: 'Step 2. Import datasets and functions.',
        description: 'Bring in data and reusable logic to start building.',
        ctaLabel: 'Import Assets',
        ctaTo: '/sources'
      };
    }

    if (!status.hasWorkspace) {
      return {
        number: 3,
        title: 'Step 3. Create a workspace.',
        description: 'Create your first workspace to begin analysis.',
        ctaLabel: 'Create Workspace',
        ctaTo: '/workspaces'
      };
    }

    return null;
  }, [status]);

  if (isLoading || !currentStep) {
    return null;
  }

  return (
    <div className="px-6 pt-4 shrink-0">
      <div className="relative overflow-hidden rounded-3xl border border-white/70 dark:border-gray-800/80 bg-gradient-to-br from-emerald-50 via-white to-cyan-50 dark:from-emerald-950/30 dark:via-gray-950 dark:to-cyan-950/20 shadow-[0_12px_40px_-20px_rgba(16,185,129,0.55)] backdrop-blur-sm">
        <div className="absolute inset-y-0 -left-16 w-44 bg-emerald-300/25 dark:bg-emerald-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -top-14 right-8 h-36 w-36 rounded-full bg-cyan-300/20 dark:bg-cyan-500/10 blur-3xl pointer-events-none" />

        <div className="relative p-5 md:p-6">
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/70 dark:border-emerald-700/60 bg-white/80 dark:bg-gray-900/80 px-3 py-1">
                <Sparkles size={12} className="text-emerald-500" />
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700 dark:text-emerald-300">
                  Quick Start
                </p>
              </div>

              <p className="text-base md:text-lg font-bold text-gray-900 dark:text-gray-100 mt-3">
                {currentStep.title}
              </p>
              <p className="text-xs md:text-sm text-gray-600 dark:text-gray-300 mt-1.5">
                {currentStep.description}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex items-center gap-2.5 rounded-2xl border border-white/80 dark:border-gray-800 bg-white/90 dark:bg-gray-900/80 px-3 py-2 shadow-sm">
                {[1, 2, 3].map((step) => {
                  const isComplete = step < currentStep.number;
                  const isActive = step === currentStep.number;

                  return (
                    <React.Fragment key={step}>
                      <div
                        className={cn(
                          'h-7 w-7 rounded-full text-[10px] font-bold flex items-center justify-center transition-all duration-200',
                          isComplete && 'bg-emerald-600 text-white shadow-sm',
                          isActive && 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-400/40',
                          !isComplete && !isActive && 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
                        )}
                      >
                        {isComplete ? <CheckCircle2 size={14} /> : step}
                      </div>
                      {step < 3 && (
                        <div
                          className={cn(
                            'h-[2px] w-4 rounded-full',
                            step < currentStep.number ? 'bg-emerald-500' : 'bg-gray-200 dark:bg-gray-700'
                          )}
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              <Link
                to={currentStep.ctaTo}
                className="inline-flex items-center gap-1.5 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100 px-4 py-2.5 text-[10px] font-bold uppercase tracking-[0.16em] transition-all shadow-md hover:shadow-lg"
              >
                {currentStep.ctaLabel}
                <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBanner;
